import { EmailOutlined, CalendarTodayOutlined, ShieldOutlined, EditOutlined, CheckOutlined, CloseOutlined } from '@mui/icons-material';
import { CardContainer, Tag } from 'finara-components';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { Navbar as AdminNavbar } from '@/components/admin/general/Navbar';
import { ErrorState, LoadingState } from '@/components/common';
import { Navbar as SuperAdminNavbar } from '@/components/super-admin/general/Navbar';
import { adminNavItems } from '@/data/admin/general/navigationData';
import { superAdminNavItems } from '@/data/super-admin/general/navigationData';
import { useUserDetails, useUpdateUser } from '@/hooks/queries/useUsers';
import { useAuth } from '@/hooks/useAuth';
import { setUserData } from '@/store/slices/authSlice';
import { showSuccessToast, showErrorToast } from '@/utils/toast';

export const ProfilePage: React.FC = () => {
  const { user, userContext } = useAuth();
  const dispatch = useDispatch();

  const { data: userDetails, isLoading, error, refetch } = useUserDetails(user?.id);
  const updateUserMutation = useUpdateUser();

  const [isEditingName, setIsEditingName] = useState(false);
  const [nameValue, setNameValue] = useState('');

  const handleEditName = () => {
    setNameValue(userDetails?.name || user?.name || '');
    setIsEditingName(true);
  };

  const handleSaveName = async () => {
    if (!user?.id || !nameValue.trim()) return;
    try {
      await updateUserMutation.mutateAsync({ userId: user.id, data: { name: nameValue.trim() } });
      dispatch(setUserData({ user: { ...user, name: nameValue.trim() }, userContext }));
      setIsEditingName(false);
      showSuccessToast('Nombre actualizado correctamente');
    } catch {
      showErrorToast('Error al actualizar el nombre');
    }
  };

  const handleCancelName = () => {
    setIsEditingName(false);
    setNameValue('');
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const NavbarComponent = isSuperAdmin ? SuperAdminNavbar : AdminNavbar;
  const navItems = isSuperAdmin ? superAdminNavItems : adminNavItems;

  const userInitials =
    userDetails?.name || user?.name
      ? (userDetails?.name || user?.name || '')
          .split(' ')
          .map(name => name[0])
          .join('')
          .toUpperCase()
      : 'AU';

  const getRoleName = (role?: string) => {
    switch (role) {
      case 'super_admin':
        return 'Super Admin';
      case 'admin':
        return 'Admin';
      case 'manager':
        return 'Manager';
      default:
        return 'Usuario';
    }
  };

  const getActiveSince = () => {
    if (!userDetails?.createdAt) return 'N/A';
    const date = new Date(userDetails.createdAt);
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  };

  const getDaysSinceActive = () => {
    if (!userDetails?.createdAt) return 0;
    const createdDate = new Date(userDetails.createdAt);
    const now = new Date();
    const diffMs = now.getTime() - createdDate.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  return (
    <div className="min-h-screen">
      <NavbarComponent navItems={navItems} userName={user?.name || 'Usuario'} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-brand-white">Mi Perfil</h1>
          <p className="text-muted mt-1">Información de tu cuenta y permisos</p>
        </div>

        {/* Loading State */}
        {isLoading && <LoadingState message="Cargando información del perfil..." />}

        {/* Error State */}
        {error && (
          <ErrorState
            title="Error al cargar el perfil"
            message="No se pudo cargar la información del perfil"
            error={error}
            showRetry
            onRetry={() => void refetch()}
          />
        )}

        {/* Profile Card */}
        {!isLoading && !error && userDetails && (
          <CardContainer>
            <div className="p-6">
              <h2 className="text-lg font-semibold text-brand-white mb-6">Información Personal</h2>

              <div className="flex items-start gap-6 mb-8">
                <div className="flex-shrink-0">
                  <div className="w-20 h-20 rounded-full flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #A6E77F 0%, #175757 100%)' }}>
                    <span className="text-2xl font-semibold text-white">{userInitials}</span>
                  </div>
                </div>
                <div className="flex-1">
                  {isEditingName ? (
                    <div className="flex items-center gap-2 mb-2">
                      <input
                        type="text"
                        value={nameValue}
                        onChange={e => setNameValue(e.target.value)}
                        className="px-3 py-1 bg-surface border border-border rounded-lg text-brand-white focus:outline-none focus:ring-1 focus:ring-brand-blue text-xl font-semibold w-full"
                      />
                      <button
                        onClick={() => void handleSaveName()}
                        disabled={updateUserMutation.isPending}
                        className="p-1 text-green-400 hover:text-green-300"
                      >
                        <CheckOutlined fontSize="small" />
                      </button>
                      <button onClick={handleCancelName} className="p-1 text-muted hover:text-brand-white">
                        <CloseOutlined fontSize="small" />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-xl font-semibold text-brand-white">
                        {userDetails.name || user?.name || 'Admin User'}
                      </h3>
                      <button onClick={handleEditName} className="p-1 text-muted hover:text-brand-white">
                        <EditOutlined fontSize="small" />
                      </button>
                    </div>
                  )}
                  <Tag variant="green">{getRoleName(user?.role)}</Tag>
                </div>
              </div>

              {/* User Details */}
              <div className="space-y-6">
                {/* Email */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/5 border border-border rounded-lg flex items-center justify-center flex-shrink-0">
                    <EmailOutlined className="text-muted" fontSize="small" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted mb-1">Correo Electrónico</p>
                    <p className="text-base font-medium text-brand-white">
                      {userDetails.email || user?.email || 'N/A'}
                    </p>
                  </div>
                </div>

                {/* Active Since */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/5 border border-border rounded-lg flex items-center justify-center flex-shrink-0">
                    <CalendarTodayOutlined className="text-muted" fontSize="small" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted mb-1">Activo Desde</p>
                    <p className="text-base font-medium text-brand-white">{getActiveSince()}</p>
                    <p className="text-sm text-muted">Hace {getDaysSinceActive()} días</p>
                  </div>
                </div>

                {/* Permissions */}
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 bg-white/5 border border-border rounded-lg flex items-center justify-center flex-shrink-0">
                    <ShieldOutlined className="text-muted" fontSize="small" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-muted mb-3">Nivel de Permisos</p>
                    <p className="text-base font-semibold text-brand-white mb-2">
                      {getRoleName(user?.role)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </CardContainer>
        )}
      </div>
    </div>
  );
};
